import { useState, useRef, useEffect, useCallback } from "react";
import { LogOut, Check } from "lucide-react";

/**
 * Swipe-to-confirm slider — designed to prevent accidental taps on destructive
 * actions like "Clock Out". Reads as "→ Swipe to Clock Out" until the thumb is
 * dragged past ~80% of the track, at which point `onConfirm` is fired.
 *
 * Uses Pointer Events for a single implementation that works on mouse, touch,
 * and stylus alike. Auto-resets if the user releases before the threshold.
 */
export default function SwipeToConfirm({
  label = "Swipe to confirm",
  confirmingLabel = "Working…",
  onConfirm,
  disabled = false,
  icon: Icon = LogOut,
  className = "",
  testId = "swipe-to-confirm",
}) {
  const trackRef = useRef(null);
  const thumbSize = 48; // px, matches h-12
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const startX = useRef(0);
  const trackW = useRef(0);

  const reset = useCallback(() => {
    setDragX(0);
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (disabled) reset();
  }, [disabled, reset]);

  const onPointerDown = (e) => {
    if (disabled || confirming) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    trackW.current = trackRef.current.getBoundingClientRect().width;
    startX.current = e.clientX;
    setIsDragging(true);
  };

  const onPointerMove = (e) => {
    if (!isDragging) return;
    const maxX = trackW.current - thumbSize - 4;
    const dx = Math.max(0, Math.min(maxX, e.clientX - startX.current));
    setDragX(dx);
  };

  const onPointerUp = async () => {
    if (!isDragging) return;
    const maxX = trackW.current - thumbSize - 4;
    const threshold = maxX * 0.85;
    if (dragX >= threshold) {
      // Snap to end and fire
      setDragX(maxX);
      setIsDragging(false);
      setConfirming(true);
      try {
        await onConfirm?.();
      } finally {
        setConfirming(false);
        setDragX(0);
      }
    } else {
      reset();
    }
  };

  return (
    <div
      ref={trackRef}
      className={
        "relative w-full h-12 rounded-full border overflow-hidden select-none touch-none " +
        (disabled
          ? "bg-slate-100 border-slate-200 opacity-60 cursor-not-allowed"
          : "bg-gradient-to-r from-red-50 to-red-100 border-red-200") +
        " " +
        className
      }
      data-testid={testId}
    >
      {/* Track label — dims as thumb slides across */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none text-sm font-medium text-red-700"
        style={{ opacity: 1 - Math.min(1, dragX / 100) }}
      >
        {confirming ? (
          confirmingLabel
        ) : (
          <>
            <span className="mr-2">→</span>
            {label}
          </>
        )}
      </div>

      {/* Thumb */}
      <button
        type="button"
        disabled={disabled || confirming}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={reset}
        style={{ transform: `translateX(${dragX}px)` }}
        className={
          "absolute left-0.5 top-0.5 h-11 w-11 rounded-full bg-white shadow-md border border-red-200 flex items-center justify-center text-red-700 " +
          (isDragging ? "" : "transition-transform duration-200 ease-out")
        }
        data-testid={testId + "-thumb"}
        aria-label={label}
      >
        {confirming ? (
          <Check className="w-5 h-5 text-emerald-600" />
        ) : (
          <Icon className="w-5 h-5" />
        )}
      </button>
    </div>
  );
}
